import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Title } from '@/components/Elements/Title/Title';
import styles from './two-factor-authentication.module.scss';
import { SignInCodeForm } from '@/components/Views/TwoFactorAuthentication/SignInCodeForm/SignInCodeForm';
import { LinkButton } from '@/components/Elements/LinkButton/LinkButton';
import { MFA_AUTH_TOKEN } from '@/constants/authentication';
import { routes } from '@/constants/routes';
import {
  useMfaAuthenticateMutation,
  useRequestMfaCodeMutation,
} from '@/generated/graphql';
import { getClientDomainType, setAuthTokensToLocalStorage } from '@/utils/auth';
import { GraphQLErrorExtensions } from '@/types/GraphQLErrorExtensions';
import { Error } from '@/enums/Error';
import {
  useNotification,
  TNotification,
} from '@spotted-zebra-uk/ui-components';
import { getSecondsLeftFromMessage } from '@/utils/errorMessage';
import { useRedirect } from '@/hooks/useRedirect';
import { useRedirectAppRouter } from '@/hooks/useRedirectAppRouter';
import { useAuthBranding } from '@/components/Providers/BrandingProvider/BrandingProvider';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const TwoFactorAuthenticationPage = () => {
  const router = useRedirectAppRouter();
  const redirectFromLocalStorage = useRedirect();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { handleMsgType } = useNotification();
  const { branding } = useAuthBranding();
  useDocumentTitle(
    branding
      ? t('twoFactorAuthentication.title')
      : t('twoFactorAuthentication.pageTitle')
  );
  const [mfaAccessToken, setMfaAccessToken] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  const generalErrorNotification = () => {
    handleMsgType({
      type: TNotification.error,
      title: t('twoFactorAuthentication.invalidCode'),
    });
  };

  const handleAuthError = (error: GraphQLErrorExtensions) => {
    const { code, message } = error?.response;

    switch (code) {
      case Error.INVALID_MFA_CODE: {
        handleMsgType({
          type: TNotification.error,
          title: t('twoFactorAuthentication.invalidCode'),
          message: `${t('twoFactorAuthentication.tryAgainOrRequestNew')}`,
        });
        break;
      }

      case Error.EXPIRED_MFA_TOKEN: {
        handleMsgType({
          type: TNotification.error,
          title: `${t('twoFactorAuthentication.sessionExpired')}`,
        });
        return router.pushWithRedirectAppParam(routes.login);
      }

      case Error.EXCEEDED_NUMBER_OF_ATTEMPTS: {
        handleMsgType({
          type: TNotification.error,
          title: t('login.failed'),
          message: t('login.dueToMultipleFailedLoginAttempts', {
            secondsLeft: getSecondsLeftFromMessage(message),
          }),
        });
        break;
      }

      case Error.MFA_REQUIRED: {
        generalErrorNotification();
        break;
      }
    }
  };

  const [mfaAuthenticate] = useMfaAuthenticateMutation({
    onCompleted(data) {
      setSubmitting(false);
      if (data?.ClientMFAAuthenticate?.authCode) {
        const { authCode, mfaCookie } = data.ClientMFAAuthenticate;
        setAuthTokensToLocalStorage(authCode, mfaCookie);

        if (searchParams.get('indirect-inv')) {
          router.pushWithIndirectInvParams(routes.signUp, { signedIn: 'true' });
        } else {
          redirectFromLocalStorage();
        }
      }
    },
    onError: ({ graphQLErrors }) => {
      setSubmitting(false);
      graphQLErrors.forEach(({ extensions }) => {
        if (extensions) {
          handleAuthError(extensions?.exception as GraphQLErrorExtensions);
        }
      });
    },
  });

  const [requestMfaCode] = useRequestMfaCodeMutation({
    onError: () => {
      generalErrorNotification();
    },
  });

  useEffect(() => {
    const token = localStorage.getItem(MFA_AUTH_TOKEN);
    if (token) {
      setMfaAccessToken(token);
    } else {
      router.pushWithRedirectAppParam(routes.login);
    }
  }, []);

  const handleSignInCodeSubmit = (mfaCode: string) => {
    setSubmitting(true);
    mfaAuthenticate({
      variables: {
        mfaAccessToken,
        mfaCode,
      },
    });
  };

  const requestNewCode = () => {
    requestMfaCode({
      variables: {
        mfaAccessToken,
        clientDomainType: getClientDomainType(),
      },
    });
  };

  return (
    <section>
      <Title
        title={t('twoFactorAuthentication.title')}
        className={styles.title}
      />
      <div className={styles.paragraphs}>
        <p>{t('twoFactorAuthentication.weSentCode')}</p>
        <p>{t('twoFactorAuthentication.pleaseEnterBelow')}</p>
      </div>
      <SignInCodeForm
        submitting={submitting}
        onSignInCodeSubmit={handleSignInCodeSubmit}
      />
      <LinkButton
        className={styles.requestNewCode}
        text={t('twoFactorAuthentication.requestNewCode')}
        onClick={requestNewCode}
      />
    </section>
  );
};

export default TwoFactorAuthenticationPage;
